import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { Button, type ButtonProps, Flex, PasswordInput } from '@inflearn/ds-react';
import _ from 'lodash-es';
import { useTranslation } from 'next-i18next';
import { type ReactNode, useEffect, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';

import { ApiError } from '../../../../../../../../resources/ApiError';
import { logger } from '../../../../../../../../resources/Logger';
import { MY_URL } from '../../../../../../../constants/urls';
import { useOpenEditCancelConfirmModal } from '../../../../../../../hooks/useOpenEditCancelConfirmModal';
import { usePasswordSettingEmailModal } from '../../../../../../../hooks/usePasswordSettingEmailModal';
import { usePasswordValidation } from '../../../../../../../hooks/usePasswordValidation';
import useClientAuthChangePasswordUpdateMutation from '../../../../../../../mutations/useClientAuthChangePasswordUpdateMutation';
import useClientAuthChangePasswordVerificationEmailCreateMutation from '../../../../../../../mutations/useClientAuthChangePasswordVerificationEmailCreateMutation';
import PasswordSettingValidator from '../../../../../../../validators/PasswordSettingValidator';
import {
  ERROR_MESSAGE,
  HELP_MESSAGE,
  SUCCESS_MESSAGE,
} from '../../../../../../../validators/constants';
import ValidationMessage from '../../../../../../common/ValidationMessage';
import { ResponseStatus } from '../../../../orders/schema';

interface EditModeProps {
  email: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const EditMode = ({ email, onClose, onSuccess }: EditModeProps) => {
  const { t } = useTranslation();
  const form = useForm<PasswordSettingValidator>({
    mode: 'onChange',
    resolver: classValidatorResolver(PasswordSettingValidator),
    defaultValues: {
      currentPassword: '',
      password: '',
      passwordConfirm: '',
    },
  });
  const { isDirty } = form.formState;
  const { onValidate, validationMessages, clear } = usePasswordValidation();
  const [isCurrentPasswordMatched] = useState(false);
  const [isNewPasswordMatched, setIsNewPasswordMatched] = useState(false);
  const { openEditCancelConfirmModal } = useOpenEditCancelConfirmModal();

  const mutation = useClientAuthChangePasswordUpdateMutation({
    onSuccess: () => {
      onSuccess();
      handleClose();
    },
    onError: (error) => {
      if (ApiError.is(error, ResponseStatus.BAD_REQUEST)) {
        form.setError('currentPassword', {
          type: 'manual',
          message: ERROR_MESSAGE.UNCONFIRMED_PASSWORD()(),
        });

        return;
      }

      logger.error(error);
    },
  });

  useEffect(() => {
    const { isDirty: isPasswordConfirmDirty, error } = form.getFieldState('passwordConfirm');
    setIsNewPasswordMatched(isPasswordConfirmDirty && _.isEmpty(error));
  }, [form.formState.errors?.passwordConfirm]);

  const clearForm = () => {
    form.reset();
    clear();
    setIsNewPasswordMatched(false);
  };

  const handleClose = () => {
    onClose?.();
    clearForm();
  };

  const handleSubmit: SubmitHandler<PasswordSettingValidator> = ({ currentPassword, password }) => {
    mutation.mutate({ oldPassword: currentPassword, newPassword: password });
  };

  const handleEditCancel = () => {
    if (!isDirty) {
      handleClose();

      return;
    }

    openEditCancelConfirmModal({
      onConfirm: handleClose,
    });
  };

  return (
    <Flex
      component="form"
      w="100%"
      direction="column"
      gap={8}
      onSubmit={form.handleSubmit(handleSubmit)}
    >
      <Flex direction="column" gap={5} w="100%">
        <PasswordInput
          placeholder={t('현재 비밀번호')}
          size="md"
          w="100%"
          autoFocus
          {...form.register('currentPassword')}
          error={
            !!form.formState.errors?.currentPassword?.message && (
              <ValidationMessage
                status="error"
                message={form.formState.errors.currentPassword.message}
              />
            )
          }
        />

        {!form.formState.errors?.currentPassword?.message && (
          <ValidationMessage
            status={isCurrentPasswordMatched ? 'success' : 'help'}
            message={
              isCurrentPasswordMatched
                ? SUCCESS_MESSAGE.MATCH(t('현재 비밀번호'))()
                : HELP_MESSAGE.INPUT_RETRY(t('현재 비밀번호를'))()
            }
          />
        )}
      </Flex>
      <Flex direction="column" gap={5} w="100%">
        <PasswordInput
          placeholder={t('새 비밀번호')}
          size="md"
          {...form.register('password')}
          onChange={(e) => {
            /** 공백 제외 */
            e.target.value = e.target.value.replace(/\s/g, '');
            form.register('password').onChange(e);
            onValidate(e.target.value);

            if (form.getFieldState('passwordConfirm').isDirty) {
              form.trigger('passwordConfirm');
            }
          }}
          error={!_.isEmpty(form.formState.errors.password?.message)}
        />

        {validationMessages.map(({ type, message, status }) => (
          <ValidationMessage key={type} status={status} message={message} />
        ))}
      </Flex>
      <Flex direction="column" gap={5} w="100%">
        <PasswordInput
          placeholder={t('새 비밀번호 확인')}
          size="md"
          w="100%"
          {...form.register('passwordConfirm')}
          error={
            !!form.formState.errors?.passwordConfirm?.message && (
              <ValidationMessage
                status="error"
                message={form.formState.errors.passwordConfirm.message}
              />
            )
          }
        />

        {!form.formState.errors?.passwordConfirm?.message && (
          <ValidationMessage
            status={isNewPasswordMatched ? 'success' : 'help'}
            message={
              isNewPasswordMatched
                ? SUCCESS_MESSAGE.MATCH(t('새 비밀번호'))()
                : HELP_MESSAGE.INPUT_RETRY(t('새 비밀번호를'))()
            }
          />
        )}
      </Flex>
      <Flex justify="space-between">
        <FindPasswordButton email={email}>{t('비밀번호 찾기')}</FindPasswordButton>
        <Flex gap={8}>
          <Button
            color="gray"
            variant="subtle"
            size="sm"
            radius="md"
            onClick={handleEditCancel}
            type="button"
          >
            {t('취소')}
          </Button>
          <Button
            type="submit"
            color="infgreen"
            variant="default"
            size="sm"
            radius="md"
            disabled={!form.formState.isValid}
            loading={mutation.isLoading}
          >
            {t('저장')}
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
};

export const FindPasswordButton = ({
  email,
  children,
  onSuccess,
  ...props
}: { email: string; children: ReactNode; onSuccess?: () => void } & ButtonProps) => {
  const { openPasswordSettingEmailModal } = usePasswordSettingEmailModal();

  const mutation = useClientAuthChangePasswordVerificationEmailCreateMutation({
    onSuccess: () => {
      onSuccess?.();
      openPasswordSettingEmailModal(email);
    },
  });

  const handleClick = () => {
    mutation.mutate({
      email,
      referUrl: MY_URL.ACCOUNT(),
    });
  };

  return (
    <Button
      variant="light"
      color="gray"
      size="sm"
      type="button"
      onClick={handleClick}
      loading={mutation.isLoading}
      loaderPosition="center"
      {...props}
    >
      {children}
    </Button>
  );
};
